import { getOpeningHoursStatus } from 'shared-helpers/courses';
import { localeLanguageCodes } from 'shared-locales';
import { useDateNow, useReduxState } from 'hooks';
import { LinkButton } from 'components/common';
import { CoursePicture } from 'components/courses/common';
import { CoursesTileContent, CoursesTileProgress } from 'components/courses';
import { CoursesTilePublishStatus } from 'components/courses/index';
import { PathStepTileContent, PathStepTilePicture } from '.';

import type { CourseProgress, PathStepItemCourse } from 'shared-types';

interface Props {
  course: PathStepItemCourse['course'] & { progressData: CourseProgress };
  stepAvailable?: boolean;
  stepEnabled?: boolean;
}

const PathStepTileCourse = ({ course, stepAvailable, stepEnabled }: Props) => {
  const { dateNow } = useDateNow();

  const {
    language,
    messages,
    serverConfig: { timeZone },
    user
  } = useReduxState();

  const courseHasNotStarted = course.startDate
    ? new Date(course.startDate).getTime() > dateNow
    : false;

  const courseHasEnded = course.endDate
    ? new Date(course.endDate).getTime() < dateNow
    : false;

  const openingHoursStatus = getOpeningHoursStatus({
    openingHours: course.openingHours,
    timeZone
  });

  const courseIsClosed =
    course.openingHours?.isEnabled && !openingHoursStatus?.isCurrentlyOpen;

  return (
    <div className="course">
      <PathStepTilePicture>
        <CoursePicture
          position={course.picturePosition}
          size={640}
          url={course.picture}
        />
      </PathStepTilePicture>

      <PathStepTileContent>
        <CoursesTileContent
          course={course}
          isCourseInPath={true}
          preferredLanguageVersion={
            course.progressData?.completion?.courseLanguageVersion ||
            localeLanguageCodes[language]
          }
        />

        <div className="footer">
          <div className="footer-content">
            <div>
              <div className="progress">
                {course.progressData ? (
                  <CoursesTileProgress
                    completionEnabled={course.completionEnabled}
                    id={`path-course-progress-${course.id}`}
                    progressData={course.progressData}
                  />
                ) : null}
              </div>
            </div>

            {stepAvailable || user?.roles.includes('superadmin') ? (
              <LinkButton
                disabled={
                  !user?.roles.includes('superadmin') &&
                  (!stepEnabled ||
                    courseHasNotStarted ||
                    courseHasEnded ||
                    courseIsClosed)
                }
                href={`/courses/${course.slug}${
                  course.chapters.length ? `/${course.chapters[0].slug}` : ''
                }`}
                isRounded={true}
                onClick={(ev) => ev.stopPropagation()}
                size="small"
                style={{ fontSize: '1.3rem', padding: '6px 12px 7px' }}
                text={messages.commonOpen}
                tooltip={
                  user?.roles.includes('superadmin')
                    ? null
                    : stepEnabled
                    ? courseHasNotStarted
                      ? messages.courseWillBecomeAvailable
                      : courseHasEnded
                      ? messages.readerErrorEnded
                      : courseIsClosed
                      ? messages.courseIsClosed
                      : null
                    : messages.pathsStepsCompletePreviousInfo
                }
              />
            ) : null}
          </div>
        </div>
      </PathStepTileContent>

      <CoursesTilePublishStatus
        endDate={course.endDate as string}
        openingHours={course.openingHours}
        startDate={course.startDate as string}
      />

      <style jsx>{`
        .footer {
          margin-top: auto;
          padding-top: 20px;
        }
        .footer-content {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default PathStepTileCourse;
